import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FilePlus2, Globe, Loader2, Microscope, Paperclip, Plus, Send, Square } from "lucide-react";

import { api, MAX_UPLOAD_BYTES, type DocumentRow } from "@/lib/api";
import { formatFileSize } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useInvalidateProject, useProject } from "@/hooks/queries/useProject";
import { useInvalidateStandaloneDocuments, useStandaloneDocuments } from "@/hooks/queries/useDocuments";
import { DOCUMENT_ACCEPT_ATTR } from "@/lib/documentTypes";
import { iconTransition, instantTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

import { CreditsExhaustedModal } from "@/components/assistant/ChatModals";
import type { AttachedDocument, ChatInputPayload, DocumentPickerItem } from "@/components/assistant/types";

export interface ChatInputHandle {
  /** Programmatically attach a document (e.g. drag from the project file tree). */
  addDoc: (doc: DocumentPickerItem) => void;
}

interface ChatInputProps {
  onSubmit: (payload: ChatInputPayload) => void;
  onStop?: () => void;
  isLoading?: boolean;
  projectId?: string;
  compact?: boolean;
  className?: string;
  webSearchEnabled?: boolean;
  onWebSearchChange?: (enabled: boolean) => void;
  workflowEnabled?: boolean;
  onWorkflowChange?: (enabled: boolean) => void;
}

function documentToAttached(doc: DocumentPickerItem): AttachedDocument {
  return { filename: doc.filename, displayName: doc.display_name ?? doc.filename, document_id: doc.id };
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ onSubmit, onStop, isLoading = false, projectId, compact = false, className, webSearchEnabled: controlledWebSearch, onWebSearchChange, workflowEnabled: controlledWorkflow, onWorkflowChange }, ref) {
  const { credits } = useUserProfile();
  const [text, setText] = useState("");
  const [attachedDocuments, setAttachedDocuments] = useState<AttachedDocument[]>([]);
  const [localWebSearch, setLocalWebSearch] = useState(false);
  const [localWorkflow, setLocalWorkflow] = useState(false);

  const webSearchEnabled = controlledWebSearch ?? localWebSearch;
  const setWebSearchEnabled = onWebSearchChange ?? setLocalWebSearch;
  const workflowEnabled = controlledWorkflow ?? localWorkflow;
  const setWorkflowEnabled = onWorkflowChange ?? setLocalWorkflow;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const iconMotion = prefersReducedMotion ? instantTransition : iconTransition;

  useEffect(() => {
    if (!projectId) {
      setLocalWorkflow(false);
      onWorkflowChange?.(false);
    }
  }, [projectId, onWorkflowChange]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  }, [text]);

  const { data: standaloneDocs } = useStandaloneDocuments();
  const projectQuery = useProject(projectId ?? "", undefined, { enabled: Boolean(projectId) });
  const invalidateProject = useInvalidateProject();
  const invalidateStandaloneDocs = useInvalidateStandaloneDocuments();
  const documents = useMemo<DocumentPickerItem[]>(
    () => [...(standaloneDocs ?? []), ...(projectQuery.data?.documents ?? [])],
    [standaloneDocs, projectQuery.data?.documents]
  );

  const canSend = text.trim().length > 0 && !isLoading && !uploading;

  const attachedIds = useMemo(() => new Set(attachedDocuments.map((doc) => doc.document_id)), [attachedDocuments]);

  useImperativeHandle(
    ref,
    () => ({
      addDoc: (doc: DocumentPickerItem) =>
        setAttachedDocuments((current) =>
          current.some((item) => item.document_id === doc.id) ? current : [...current, documentToAttached(doc)]
        )
    }),
    []
  );

  const submit = useCallback(() => {
    const content = text.trim();
    if (!content || isLoading) {
      return;
    }

    if (credits !== null && credits <= 0) {
      setCreditsModalOpen(true);
      return;
    }

    onSubmit({ content, attachedDocuments, webSearchEnabled, workflowEnabled: projectId ? workflowEnabled : false });
    setText("");
    setAttachedDocuments([]);
    setUploadError(null);
    setCreditsModalOpen(false);
  }, [attachedDocuments, credits, isLoading, onSubmit, projectId, text, webSearchEnabled, workflowEnabled]);

  return (
    <div className={cn("vadium-chat-composer w-full rounded-md border bg-card p-3 shadow-sm", className)}>
      <Textarea
        data-chat-textarea
        ref={textareaRef}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
        rows={compact ? 1 : 3}
        placeholder="Ask Vadium to read, compare, draft, or edit documents..."
        className="max-h-56 min-h-12 w-full resize-none border-0 bg-transparent p-0 leading-6 shadow-none outline-none focus-visible:ring-0"
      />

      {attachedDocuments.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {attachedDocuments.map((doc) => (
            <Chip
              key={doc.document_id}
              variant="muted"
              size="md"
              className="h-7 max-w-64"
              onRemove={() => setAttachedDocuments((current) => current.filter((item) => item.document_id !== doc.document_id))}
              removeLabel={`Remove ${doc.displayName ?? doc.filename}`}
            >
              <span className="truncate">{doc.displayName ?? doc.filename}</span>
            </Chip>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="vadium-hit-target size-9 rounded-full" aria-label="Add tools and attachments">
              <Plus className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuItem inset disabled={uploading} onClick={() => fileInputRef.current?.click()} className="gap-2">
              {uploading ? <Loader2 className="size-4 animate-spin" /> : <Paperclip className="size-4" />}
              {uploading ? "Uploading…" : "Upload file"}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger inset className="gap-2">
                <FilePlus2 className="size-4" />
                Attach document
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-80 w-80 overflow-y-auto">
                  {documents.length ? (
                    documents.map((doc) => (
                      <DropdownMenuItem
                        key={doc.id}
                        disabled={attachedIds.has(doc.id)}
                        onClick={() => setAttachedDocuments((current) => [...current, documentToAttached(doc)])}
                        className="min-w-0"
                      >
                        <span className="truncate">{doc.display_name ?? doc.filename}</span>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <DropdownMenuItem disabled>No documents found</DropdownMenuItem>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem className="gap-2" checked={webSearchEnabled} onCheckedChange={setWebSearchEnabled} onSelect={(event) => event.preventDefault()}>
              <Globe className="size-4" />
              Web search
            </DropdownMenuCheckboxItem>
            {projectId ? (
              <DropdownMenuCheckboxItem className="gap-2" checked={workflowEnabled} onCheckedChange={setWorkflowEnabled} onSelect={(event) => event.preventDefault()}>
                <Microscope className="size-4" />
                Corpus review
              </DropdownMenuCheckboxItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>

        <AnimatePresence initial={false}>
          {webSearchEnabled ? (
            <motion.span key="chip-web-search" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} transition={iconMotion}>
              <Chip variant="muted" size="md" className="h-7" icon={<Globe />} onRemove={() => setWebSearchEnabled(false)} removeLabel="Disable web search">
                {compact ? null : <span className="hidden sm:inline">Web search</span>}
              </Chip>
            </motion.span>
          ) : null}
          {projectId && workflowEnabled ? (
            <motion.span key="chip-corpus-review" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} transition={iconMotion}>
              <Chip variant="muted" size="md" className="h-7" icon={<Microscope />} onRemove={() => setWorkflowEnabled(false)} removeLabel="Disable corpus review">
                {compact ? null : <span className="hidden sm:inline">Corpus review</span>}
              </Chip>
            </motion.span>
          ) : null}
          {uploading ? (
            <motion.span key="chip-uploading" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} transition={iconMotion}>
              <Chip variant="muted" size="md" className="h-7" icon={<Loader2 className="animate-spin" />}>
                {compact ? null : <span className="hidden sm:inline">Uploading…</span>}
              </Chip>
            </motion.span>
          ) : null}
        </AnimatePresence>
        <input
          ref={fileInputRef}
          type="file"
          accept={DOCUMENT_ACCEPT_ATTR}
          className="hidden"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            event.target.value = "";
            if (file.size > MAX_UPLOAD_BYTES) {
              setUploadError(`${file.name} (${formatFileSize(file.size)}) exceeds ${formatFileSize(MAX_UPLOAD_BYTES)} limit`);
              return;
            }
            setUploading(true);
            setUploadError(null);
            try {
              let doc: DocumentRow;
              if (projectId) {
                doc = await api.projects.uploadDocument(projectId, file);
              } else {
                doc = await api.documents.uploadStandalone(file);
              }
              // Attach first — the upload succeeded; cache invalidation below
              // is housekeeping and must not surface as an upload failure.
              setAttachedDocuments((current) =>
                current.some((item) => item.document_id === doc.id)
                  ? current
                  : [...current, { filename: doc.filename, displayName: doc.display_name ?? doc.filename, document_id: doc.id }]
              );
              try {
                if (projectId) {
                  await invalidateProject(projectId);
                } else {
                  await invalidateStandaloneDocs();
                }
              } catch {
                // best-effort
              }
            } catch (err) {
              setUploadError(err instanceof Error ? err.message : "Upload failed");
            } finally {
              setUploading(false);
            }
          }}
        />

        <div className="ml-auto flex items-center gap-2">
          <Button type="button" size="icon" className="vadium-hit-target size-9" disabled={!canSend && !isLoading} onClick={isLoading ? onStop : submit} aria-label={isLoading ? "Stop response" : "Send message"}>
            <span className="relative flex size-4 items-center justify-center">
              <AnimatePresence initial={false}>
                {isLoading ? (
                  <motion.span
                    key="stop"
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={iconMotion}
                  >
                    <Square className="size-4 fill-current" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="send"
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={iconMotion}
                  >
                    <Send className="size-4" />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </Button>
        </div>
      </div>
      {uploadError ? (
        <p role="alert" className="mt-2 text-xs text-destructive">{uploadError}</p>
      ) : null}
      <CreditsExhaustedModal open={creditsModalOpen} onClose={() => setCreditsModalOpen(false)} />
    </div>
  );
});
